// src/components/Profile.tsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import ProfilePostsGrid from './ProfilePostsGrid.tsx';
import FollowingModal from './FollowingModal.tsx';
import Cropper from 'react-easy-crop';
import { useNavigate } from 'react-router-dom';
import { MessagingIcon } from './messaging/MessagingIcon.tsx';
import { MessagingIconSimple } from './messaging/MessagingIconSimple.tsx';

interface UserStats {
  followers: number;
  following: number;
  posts: number;
}

interface ProfileProps {
  onViewUser?: (userId: string) => void;
}

export function Profile({ onViewUser }: ProfileProps) {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth(); // Add updateUser
  const [currentBio, setCurrentBio] = useState(''); // Track current bio locally
  const [stats, setStats] = useState<UserStats>({ followers: 0, following: 0, posts: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [showFollowing, setShowFollowing] = useState<'following' | 'followers' | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    fitness_goals: '',
    phone_number: '',
  });
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    if (user) {
      // Initialize edit data with current user info
      console.log('User object on profile load:', user);
      console.log('User bio:', user.bio);
      console.log('User fitness_goals:', user.fitness_goals);

      setEditData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        bio: user.bio || '',
        fitness_goals: user.fitness_goals || '',
        phone_number: user.phone_number || '',
      });

      // Initialize current bio
      setCurrentBio(user.bio || '');

      // Fetch real stats
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    if (!user) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://aztrainersbackend.onrender.com/api/users/${user.id}/stats/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          followers: data.followers_count || 0,
          following: data.following_count || 0,
          posts: data.posts_count || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default values if fetch fails
      setStats({ followers: 0, following: 0, posts: 0 });
    }
  };

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc: string, pixelCrop: any): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Set canvas size to the crop size
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, 'image/jpeg', 0.8);
    });
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageToCrop(e.target?.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    try {
      const croppedImageBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);

      // Convert blob to file
      const croppedFile = new File([croppedImageBlob], 'profile_picture.jpg', {
        type: 'image/jpeg',
      });

      // Upload the cropped image
      await uploadProfilePicture(croppedFile);

      // Close crop modal
      setShowCropModal(false);
      setImageToCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);

    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Failed to crop image. Please try again.');
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const uploadProfilePicture = async (file: File) => {
    if (!user) return;

    try {
      const formData = new FormData();
      formData.append('profile_picture', file);

      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://aztrainersbackend.onrender.com/api/users/${user.id}/profile-picture/`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Profile picture uploaded successfully:', data);

        // Update AuthContext with new profile picture URL
        if (data.profile_picture_url) {
          updateUser({ profile_picture: data.profile_picture_url });
        }

        // Update preview immediately
        const reader = new FileReader();
        reader.onload = (e) => {
          setProfilePicturePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        // Clear the file input
        setProfilePicture(null);
      } else {
        console.error('Failed to upload profile picture');
        alert('Failed to upload profile picture. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      alert('Failed to upload profile picture. Please try again.');
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const jsonData = {
        first_name: editData.first_name,
        last_name: editData.last_name,
        bio: editData.bio,
        phone_number: editData.phone_number,
        ...(user.role === 'customer' && { fitness_goals: editData.fitness_goals })
      };

      const token = localStorage.getItem('authToken');

      const response = await fetch(`https://aztrainersbackend.onrender.com/api/users/${user.id}/profile/`, {
        method: 'PUT',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData)
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('Profile updated successfully');

        // Update local bio state to reflect changes immediately
        setCurrentBio(editData.bio);

        // Update AuthContext user object with new data
        updateUser({
          first_name: editData.first_name,
          last_name: editData.last_name,
          bio: editData.bio,
          phone_number: editData.phone_number,
          name: `${editData.first_name} ${editData.last_name}`.trim(),
          fitness_goals: editData.fitness_goals
        });

        // Exit edit mode
        setIsEditing(false);

        // Clear profile picture preview (since it's already uploaded)
        setProfilePicturePreview(null);

      } else {
        console.error('Failed to update profile');
        const errorText = await response.text();
        console.error('Error response:', errorText);
        alert('Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-gray-600">Please log in to view your profile</p>
      </div>
    );
  }

  const isTrainer = user.role === 'trainer';
  const isAdmin = user.role === 'admin';
  const fullName = `${user.first_name} ${user.last_name}`.trim() || user.name;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Profile Header */}
      <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg overflow-hidden mb-6">
        <div className="px-6 pt-6 pb-6 relative">
          {/* Top Right Controls - Updated */}
          <div className="absolute top-4 right-4 flex items-center space-x-2">
            {/* Add Messaging Icon */}
            <MessagingIconSimple
              className="bg-white/20 backdrop-blur-sm border border-gray-200"
            />

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="bg-white/20 backdrop-blur-sm text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-white/30 transition-colors border border-gray-200"
            >
              Logout
            </button>
          </div>

          {/* Profile Picture and Basic Info */}
          <div className="flex items-end justify-between mb-4">
            <div className="flex items-end space-x-4">
              <div className="relative">
                <div className="w-32 h-32 bg-gray-300 rounded-full border-4 border-white shadow-lg overflow-hidden">
                  {profilePicturePreview ? (
                    <img
                      src={profilePicturePreview}
                      alt="Profile preview"
                      className="w-full h-full object-cover"
                    />
                  ) : user.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold text-2xl">
                      {user.first_name?.[0] || user.name?.[0] || user.username[0]}
                      {user.last_name?.[0] || ''}
                    </div>
                  )}
                </div>

                {/* Profile Picture Upload Button (only when editing) */}
                {isEditing && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                    <label htmlFor="profile-picture-upload" className="cursor-pointer">
                      <div className="bg-white rounded-full p-2 hover:bg-gray-100 transition-colors">
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                    </label>
                    <input
                      id="profile-picture-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
              <div className="pb-2">
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      value={editData.first_name}
                      onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                      placeholder="First Name"
                      className="text-xl font-semibold text-gray-800 bg-white/80 border border-gray-200 rounded px-2 py-1 mr-2"
                    />
                    <input
                      value={editData.last_name}
                      onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                      placeholder="Last Name"
                      className="text-xl font-semibold text-gray-800 bg-white/80 border border-gray-200 rounded px-2 py-1"
                    />

                  </div>
                ) : (
                  <h1 className="text-2xl font-semibold text-gray-800">{fullName}</h1>
                )}
                <p className="text-gray-600">@{user.username}</p>
                <p className="text-gray-500 text-sm">{user.email}</p>
                {isTrainer && (
                  <span className="inline-block bg-[#9CAF88] text-[#FEFCF8] text-sm px-3 py-1 rounded-full mt-2 font-medium">
                    Certified Trainer
                  </span>
                )}
                {isAdmin && (
                  <span className="inline-block bg-[#8B5A3C] text-[#FEFCF8] text-sm px-3 py-1 rounded-full mt-2 font-medium">
                    Administrator
                  </span>
                )}
              </div>
            </div>

            <div className="flex space-x-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#9CAF88] text-[#FEFCF8] px-4 py-2 rounded-full hover:bg-[#7A8471] transition-colors disabled:opacity-50 font-medium"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      // Reset profile picture states when canceling
                      setProfilePicture(null);
                      setProfilePicturePreview(null);
                      // Reset edit data to original values
                      setEditData({
                        first_name: user.first_name || '',
                        last_name: user.last_name || '',
                        bio: user.bio || '',
                        fitness_goals: user.fitness_goals || '',
                        phone_number: user.phone_number || '',
                      });
                    }}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-full hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-white text-gray-800 px-4 py-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>

          {/* User Role Info */}
          <div className="mb-4 p-3 bg-white/40 rounded-lg">
            <h3 className="text-gray-800 font-semibold">Account Type</h3>
            <p className="text-gray-700 capitalize">{user.role}</p>
            <p className="text-gray-600 text-sm">User ID: {user.id}</p>
          </div>

          {/* Phone Number */}
          <div className="mb-4 p-3 bg-white/40 rounded-lg">
            <h3 className="text-gray-800 font-semibold mb-2">Phone Number</h3>
            {isEditing ? (
              <input
                type="tel"
                value={editData.phone_number}
                onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })}
                placeholder="(555) 123-4567"
                className="w-full p-2 bg-white/80 border border-gray-200 rounded text-gray-700"
              />
            ) : (
              <p className="text-gray-700">{user.phone_number || 'Not provided'}</p>
            )}
          </div>

          {/* Bio */}
          <div className="mb-4">
            <h3 className="text-gray-800 mb-2">Bio</h3>
            {isEditing ? (
              <textarea
                value={editData.bio}
                onChange={(e) => {
                  setEditData({ ...editData, bio: e.target.value });
                }}
                placeholder="Tell us about yourself..."
                className="w-full p-2 bg-white/80 border border-gray-200 rounded text-gray-700 resize-none"
                rows={3}
              />
            ) : (
              <p className="text-gray-700">
                {currentBio || (
                  isAdmin ? 'Platform administrator with full access to manage trainers and clients.' :
                    isTrainer ? 'Certified fitness trainer helping clients reach their goals.' :
                      'Fitness enthusiast on a journey to better health.'
                )}
              </p>
            )}
          </div>

          {/* Fitness Goals (for clients) */}
          {user.role === 'customer' && (
            <div className="mb-4">
              <h3 className="text-gray-800 mb-2">Fitness Goals</h3>
              {isEditing ? (
                <textarea
                  value={editData.fitness_goals}
                  onChange={(e) => setEditData({ ...editData, fitness_goals: e.target.value })}
                  placeholder="What are your fitness goals?"
                  className="w-full p-2 bg-white/80 border border-gray-200 rounded text-gray-700 resize-none"
                  rows={2}
                />
              ) : (
                <p className="text-gray-700">{user.fitness_goals || ''}</p>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="flex space-x-8 mb-6">
            <div className="text-center">
              <div className="text-xl font-semibold text-gray-800">{stats.posts}</div>
              <div className="text-gray-600">Posts</div>
            </div>
            <button
              onClick={() => setShowFollowing('followers')}
              className="text-center hover:bg-white/20 rounded p-2 transition-colors"
            >
              <div className="text-xl font-semibold text-gray-800">{stats.followers}</div>
              <div className="text-gray-600">Followers</div>
            </button>
            <button
              onClick={() => setShowFollowing('following')}
              className="text-center hover:bg-white/20 rounded p-2 transition-colors"
            >
              <div className="text-xl font-semibold text-gray-800">{stats.following}</div>
              <div className="text-gray-600">Following</div>
            </button>
          </div>

          {/* Role-specific sections */}
          {isAdmin && (
            <div className="mb-4">
              <button
                onClick={() => navigate('/admin-panel')}
                className="w-full bg-[#8B5A3C] text-[#FEFCF8] py-3 rounded-lg hover:bg-[#5D4037] transition-colors font-medium"
              >
                Access Admin Panel
              </button>
            </div>
          )}

          {isTrainer && (
            <div className="mb-4">
              <button
                onClick={() => navigate('/trainer-dashboard')}
                className="w-full bg-[#9CAF88] text-[#FEFCF8] py-3 rounded-lg hover:bg-[#7A8471] transition-colors font-medium"
              >
                Manage Trainer Dashboard
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && imageToCrop && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">Crop Profile Picture</h3>
              <button
                onClick={handleCropCancel}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Crop Area - Flexible height */}
            <div className="relative flex-1 bg-gray-100 min-h-[300px]">
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom Control */}
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zoom
              </label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Actions - Always visible at bottom */}
            <div className="p-4 flex space-x-3 flex-shrink-0">
              <button
                onClick={handleCropCancel}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCropSave}
                className="flex-1 bg-[#9CAF88] text-[#FEFCF8] px-4 py-2 rounded-lg hover:bg-[#7A8471] transition-colors font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Following/Followers Modal */}
      <FollowingModal
        isOpen={showFollowing !== null}
        onClose={() => setShowFollowing(null)}
        onViewUser={(userId) => {
          if (onViewUser) {
            onViewUser(userId);
          }
          setShowFollowing(null); // Close modal after navigation
        }}
        type={showFollowing || 'following'}
      />

      {/* Posts Grid - Instagram Style */}
      <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Posts</h3>
        <ProfilePostsGrid />
      </div>
    </div>
  );
}